/**
 * @author: lv.chen
 * @date: 2025/8/7
 * @desc: IReportForm组件
 */
import React, { useRef, useMemo, useState, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { history, useIntl } from 'umi';
import { useDebounceFn } from 'ahooks';
import useRequest from '@gp/hooks/useRequest';
import { useForm, useField } from 'react-final-form-hooks';
import { Button, Modal, Form, Input, TextArea, Switch } from 'tea-component';
import CardSlotV2 from '@intelligence/components/CardSlotV2';
import FileNameCard from '@user-center/components/FileNameCard';
import ReportUploader from '../ReportUploader';
import LocationSelect from '../LocationSelect';
import PermissionSelect from '../PermissionSelect';
import ImageUploader from '../ImageUploader';
import CollaboratorEditor from '../CollaboratorEditor';
import ReportPreviewModal from '../ReportPreviewModal';
import useCOS, { getUseAccelerateByENV } from '@gp/hooks/useCos/useCOS';
import { recognizeReportService } from '@home/services/report';
import { getReportLanguages } from '@intelligence/utils/reportLanguage';
import { fetchUserInformation } from '@user-center/services/common';
import {
  fetchReportGZTencentQCouldMassage,
  fetchReportSGTencentQCouldMassage,
  uploadHTMLZipReport,
  addReportAuthPaths,
} from '../../services';
import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import { getIsHTMLReportPath, getIsZipReportPath, parseReportOriginalFilePaths } from '@home/utils/reportPreview';
import { formatTeaFormItemStatus, getNullableURL } from '@user-center/utils';
import {
  formatPermissions,
  buildReport,
  getIsAvailable,
  handleValidate,
  getDefaultReportCoverFileName,
  getFileNameFromPath,
  getImageFileDataCrossOriginPossibility,
  getDefaultReportLanguageNameCN,
  getDefaultReportLanguageNameEN,
  getLocationOptionsWithDisabled,
} from '../../utils';
import {
  fetchUserPermissions,
  filterCategoriesByPermission,
  validateCategoryPermission,
} from '../../utils/permissions';
import { DBI_FILE_COS_PATH, DBI_IMG_COS_PATH } from '@user-center/_config';
import { LOCATION_OPTIONS, DEFAULT_FORM_MODEL } from '../../constants';

import type { CascaderOption } from 'tea-component';

import './index.scss';

import IMG_AI_TEXT from '@home/assets/img/ai-text.svg';

const iconList: any[] = [];

const showStatusIcon = false;

const MAX_COLLABORATOR_COUNT = 2;

const createEmptyCollaborator = (): IReportCollaboratorFormModel => ({
  open_id: '',
  creator_name: '',
  creator_name_en: '',
  creator_department: '',
  creator_department_en: '',
  email: '',
  creator_avatar: null,
});

interface IReportShowFileTipsMap {
  showFileTips: boolean;
  showFileENTips: boolean;
}

export interface IReportFormInstance {
  preview: (report?: IReport) => void;
  initialize: (report: IReport) => void;
  initializeWhenRecognizedReport: (file: File, result: any) => void;
  updateCreatorInformation: (openId: string) => void;
}

interface IReportFormProps extends IBaseProps {
  reportPublishLoading: boolean;
  permissionOptions: CascaderOption[];
  onConfirm?: (values: IReportPartial) => void;
  onBack?: () => void;
  onPreview?: () => void;
  onPublish?: (values: IReportPartial) => void;
  onUploadCover?: (file: File) => void;
  onUploadCreatorAvatar?: (file: File) => void;
}

const buildReportFileData = (path: string, originalPath = ''): IReportFileData | null => {
  if (!path) return null;

  return {
    key: path,
    originalKey: originalPath,
    url: '',
    file_name: getFileNameFromPath(originalPath || path),
    file: null,
  };
};

const ReportForm: React.ForwardRefRenderFunction<IReportFormInstance, IReportFormProps> = (props, ref) => {
  const {
    style,
    className,
    reportPublishLoading,
    permissionOptions,
    onConfirm,
    onBack,
    onPreview,
    onPublish,
    onUploadCover,
    onUploadCreatorAvatar,
  } = props;

  // 仅预发布、正式 支持COS全球域名加速请求
  const useAccelerate = getUseAccelerateByENV();

  const { formatMessage } = useIntl();

  const { putObject } = useCOS({
    useAccelerate,
    baseCOSConfigFetcher: fetchReportGZTencentQCouldMassage,
    authorizationFetcher: fetchReportGZTencentQCouldMassage,
  });

  const { getObjectUrl: getObjectUrlSG, putObject: putObjectSG } = useCOS({
    useAccelerate,
    baseCOSConfigFetcher: fetchReportSGTencentQCouldMassage,
    authorizationFetcher: fetchReportSGTencentQCouldMassage,
  });

  const { data: userPermissions } = useRequest(fetchUserPermissions, {
    onError: (error) => {
      console.error('Failed to fetch user permissions:', error);
    },
  });

  const filteredLocationOptions = useMemo(() => {
    return filterCategoriesByPermission(LOCATION_OPTIONS, userPermissions || { grc: false, iic: false });
  }, [userPermissions]);

  const validate = (values: IReportFormModel) => {
    const errors = handleValidate(values);

    const { location } = values || {};
    if (location && location.length > 0) {
      const isValid = validateCategoryPermission(location[0], userPermissions || { grc: false, iic: false });
      if (!isValid) {
        errors.location = formatMessage({ id: 'dbu_report_add_update_error_tips1' });
      }
    }
    return errors;
  };

  const { form, values, submitting, handleSubmit } = useForm({
    initialValues: DEFAULT_FORM_MODEL,
    validate,
    onSubmit: async (values: IReportFormModel) => {
      try {
        const [uploadedReportFiles, uploadedImageValues] = await Promise.all([
          uploadFileAndFileEN(values),
          uploadCoverAndCreatorAvatarByValues(values),
        ]);
        const fileNew = uploadedReportFiles.file;
        const fileENNew = uploadedReportFiles.fileEN;
        const valuesNew = {
          ...uploadedImageValues,
          file: fileNew,
          file_en: fileENNew,
        };

        form.batch(() => {
          form.change('file', fileNew);
          form.change('file_en', fileENNew);
          form.change('cover', uploadedImageValues.cover);
          form.change('creator_avatar', uploadedImageValues.creator_avatar);
          form.change('collaborators', uploadedImageValues.collaborators);
        });

        const report = buildReport(valuesNew, { permissionOptions, reportHelperInfo: reportHelperInfo.current });
        await onConfirm?.(report);
        setSubmitSuccess(true);
        setLocationRestrictionEnabled(true);
      } catch (error) {
        console.error(error);
        setSubmitSuccess(false);
      } finally {
        setSubmitted(false);
      }
    },
  });

  const fileField = useField('file', form);
  const fileENField = useField('file_en', form);
  const titleField = useField('title', form);
  const titleENField = useField('title_en', form);
  const introductionField = useField('introduction', form);
  const introductionENField = useField('introduction_en', form);
  const tagField = useField('tag', form);
  const tagENField = useField('tag_en', form);
  const coverField = useField('cover', form);
  const locationField = useField('location', form);
  const permissionField = useField('permission', form);
  const creatorNameENField = useField('creator_name_en', form);
  const creatorDepartmentENField = useField('creator_department_en', form);
  const downloadEnableField = useField('download_enable', form);
  const watermarkEnableField = useField('watermark_enable', form);

  const file = values?.file;
  const fileName = file?.file_name || '';

  const fileEN = values?.file_en;
  const fileENName = fileEN?.file_name || '';

  const cover = values?.cover;
  const coverUrl = cover?.url || '';

  const creatorAvatar = values?.creator_avatar;
  const collaborators: IReportCollaboratorFormModel[] = values?.collaborators || [];

  /**
   * 报告的其他信息（报告AI读取到，根据open_id自动拉取到的，但是页面表单又不展示，不提供修改入口的信息，新增编辑报告需要用到）
   */
  const reportHelperInfo = useRef({
    report_language: '',
    report_language_en: '',
    file_cn_language: '',
    file_en_language: '',
    creator_name: '',
    creator_department: '',
    collaborators: [] as Array<Pick<IReportCollaboratorFormModel, 'creator_name' | 'creator_department'>>,
  });

  const collaboratorInformationTimers = useRef<Record<number, number>>({});

  const primaryAuthor: IReportCollaboratorFormModel = {
    open_id: creatorNameENField.input.value || '',
    creator_name: reportHelperInfo.current.creator_name,
    creator_name_en: creatorNameENField.input.value || '',
    creator_department: reportHelperInfo.current.creator_department,
    creator_department_en: creatorDepartmentENField.input.value || '',
    creator_avatar: creatorAvatar,
  };

  const formAvailable = useMemo<boolean>(() => getIsAvailable(values), [values]);
  const formValid = useMemo<boolean>(() => isEmpty(handleValidate(values)), [values]);
  const authorOpenIds = [values?.creator_name_en || '', ...collaborators.map((c) => c.open_id || '')];

  const [reportShowFileTipsMap, setReportShowFileTipsMap] = useState<IReportShowFileTipsMap>({
    showFileTips: false,
    showFileENTips: false,
  });

  const [submitSuccess, setSubmitSuccess] = useState(false);

  /**
   * 是否启用 Location 选择限制
   * - Edit 模式：初始化后启用
   * - Add 模式：保存成功后启用
   */
  const [locationRestrictionEnabled, setLocationRestrictionEnabled] = useState(false);

  /**
   * 计算带 disabled 字段的 Location options
   * 禁用规则：
   * 1. 当前选中 Game Research Report 时，禁用其他所有一级分类
   * 2. 当前选中非 Game Research Report 时，禁用 Game Research Report
   */
  const locationOptionsWithDisabled = useMemo(() => {
    if (!locationRestrictionEnabled) {
      return filteredLocationOptions;
    }
    const currentL1Value = values?.location?.[0];
    return getLocationOptionsWithDisabled(filteredLocationOptions, currentL1Value);
  }, [values?.location, filteredLocationOptions, locationRestrictionEnabled]);

  const [submitted, setSubmitted] = useState(false);

  const [coverLoading, setCoverLoading] = useState(false);

  const [creatorAvatarLoading, setCreatorAvatarLoading] = useState(false);

  const [previewLoading, setPreviewLoading] = useState(false);

  const [previewVisible, setPreviewVisible] = useState(false);

  const [previewReport, setPreviewReport] = useState<IReportPartial | null>(null);

  const handleSubmitInPage = () => {
    setSubmitted(true);
    handleSubmit();
  };

  const updateCoverSilent = async (params: { fileName: string; cover: string; timestamp: number; }) => {
    const { fileName, cover, timestamp } = params || {};

    try {
      setCoverLoading(true);

      const coverFileNameInitial = getDefaultReportCoverFileName(fileName);

      const {
        fileKey: coverKey,
        fileName: coverFileName,
        fileUrl: coverUrl,
        file: coverFile,
      } = await getImageFileDataCrossOriginPossibility({
        url: cover,
        getFileKey: () => `${DBI_IMG_COS_PATH}/${timestamp}_${coverFileNameInitial}`,
        getFileName: () => coverFileNameInitial,
      });

      form.batch(() => {
        form.change('cover', {
          key: coverKey,
          url: coverUrl,
          file_name: coverFileName,
          file: coverFile,
        });
      });
    } catch (error) {
      console.error(error);
    } finally {
      setCoverLoading(false);
    }
  };

  /**
   * 通用的报告识别处理方法
   */
  const createRecognizeHandler = (isEN: boolean) => {
    return (file: File, result: any) => {
      const fileName = file?.name || '';
      const recognizedReportData = result?.data || {};
      const language = recognizedReportData?.report_language || getDefaultReportLanguageNameCN(isEN);
      const languageEN = recognizedReportData?.report_language_en || getDefaultReportLanguageNameEN(isEN);
      const title = recognizedReportData?.title || '';
      const titleEN = recognizedReportData?.title_en || '';
      const introduction = recognizedReportData?.introduction || '';
      const introductionEN = recognizedReportData?.introduction_en || '';
      const tag = recognizedReportData?.tag?.join(' | ') || '';
      const tagEN = recognizedReportData?.tag_en?.join(' | ') || '';
      const cover = recognizedReportData?.cover || '';

      const detectedLang = recognizedReportData?.report_language_en || (isEN ? 'English' : 'Chinese');
      if (isEN) {
        reportHelperInfo.current.file_en_language = detectedLang;
      } else {
        reportHelperInfo.current.file_cn_language = detectedLang;
      }

      if (!isEN || !reportHelperInfo.current.report_language) {
        reportHelperInfo.current.report_language = language;
      }
      if (!isEN || !reportHelperInfo.current.report_language_en) {
        reportHelperInfo.current.report_language_en = languageEN;
      }

      const fileNameField = isEN ? 'file_en' : 'file';
      const langPrefix = isEN ? 'en_' : '';
      const timestamp = Date.now();

      form.batch(() => {
        form.change(fileNameField, {
          key: `${DBI_FILE_COS_PATH}/${timestamp}_${langPrefix}${fileName}`,
          url: '',
          file_name: fileName,
          file,
        });

        if (isEN) {
          // 英文文件识别，更新英文字段，条件更新中文字段
          if (!!titleEN) {
            form.change('title_en', titleEN);
          }
          if (!!introductionEN) {
            form.change('introduction_en', introductionEN);
          }
          if (!!tagEN) {
            form.change('tag_en', tagEN);
          }

          if (!values?.title) {
            form.change('title', title);
          }
          if (!values?.introduction) {
            form.change('introduction', introduction);
          }
          if (!values?.tag) {
            form.change('tag', tag);
          }
        } else {
          // 中文文件识别，更新中文字段，条件更新英文字段
          if (!!title) {
            form.change('title', title);
          }
          if (!!introduction) {
            form.change('introduction', introduction);
          }
          if (!!tag) {
            form.change('tag', tag);
          }

          if (!values?.title_en) {
            form.change('title_en', titleEN);
          }
          if (!values?.introduction_en) {
            form.change('introduction_en', introductionEN);
          }
          if (!values?.tag_en) {
            form.change('tag_en', tagEN);
          }
        }
      });

      setReportShowFileTipsMap({
        showFileTips: isEN ? !values?.file?.file : false,
        showFileENTips: isEN ? false : !values?.file_en?.file,
      });

      if (!!cover) {
        form.change('cover', null);
        updateCoverSilent({ fileName, cover, timestamp });
      }
    };
  };

  const handleRecognize = createRecognizeHandler(false);

  const handleRecognizeEN = createRecognizeHandler(true);

  const updateCreatorAvatarSilent = async (creatorUserName: string, avatar: string) => {
    try {
      setCreatorAvatarLoading(true);
      const creatorAvatarFileNameInitial = `${creatorUserName}.jpeg`;

      const {
        fileKey: creatorAvatarKey,
        fileName: creatorAvatarFileName,
        fileUrl: creatorAvatarUrl,
        file: creatorAvatarFile,
      } = await getImageFileDataCrossOriginPossibility({
        url: avatar,
        getFileKey: () => `${DBI_IMG_COS_PATH}/report-avatar-${creatorAvatarFileNameInitial}`,
        getFileName: () => creatorAvatarFileNameInitial,
      });

      form.batch(() => {
        form.change('creator_avatar', {
          key: creatorAvatarKey,
          url: creatorAvatarUrl,
          file_name: creatorAvatarFileName,
          file: creatorAvatarFile,
        });
      });
    } catch (error) {
      console.error(error);
    } finally {
      setCreatorAvatarLoading(false);
    }
  };

  const updateCreatorInformation = async (creatorUserName: string) => {
    const res = await fetchUserInformation({ username: creatorUserName });
    const resData = res?.data;
    const userFullName = resData?.full_name || '';
    const userName = resData?.user_name || '';
    const avatar = resData?.user_pic || '';
    const departmentName = resData?.department_name || '';

    reportHelperInfo.current.creator_name = userFullName || userName || creatorUserName;
    reportHelperInfo.current.creator_department = departmentName;

    form.batch(() => {
      form.change('creator_avatar', null);

      form.change('creator_department_en', departmentName);
    });

    updateCreatorAvatarSilent(creatorUserName, avatar);
  };

  const { run: updateCreatorInformationDebounce } = useDebounceFn(updateCreatorInformation, { wait: 600 });

  const uploadReportFile = async (fileData?: IReportFileData | null): Promise<IReportFileData | null> => {
    if (!fileData) {
      return null;
    }
    if (!fileData?.key || !fileData?.file) {
      return { ...fileData };
    }

    const reportFile = fileData.file;
    const isHTMLZipReport = getIsHTMLReportPath(reportFile.name) || getIsZipReportPath(reportFile.name);
    if (!isHTMLZipReport) {
      await putObject({ key: fileData.key, file: reportFile });
      return { ...fileData, originalKey: '', file: null };
    }

    const reportId = `g-${uuidv4()}`;
    const [response] = await Promise.all([
      uploadHTMLZipReport({ file: reportFile, key: reportId }),
      putObject({ key: fileData.key, file: reportFile }),
    ]);
    const htmlPath = response?.data?.htmlPath || '';
    if (!htmlPath) {
      throw new Error('HTML/ZIP report upload did not return htmlPath.');
    }
    const htmlFiles = response?.data?.htmlFiles || [];
    const paths = Array.from(new Set([htmlPath, ...htmlFiles].filter(Boolean)));
    await addReportAuthPaths({
      report_id: reportId,
      paths,
    });
    return {
      ...fileData,
      key: htmlPath,
      originalKey: fileData.key,
      file: null,
    };
  };

  const uploadFileAndFileEN = async (
    reportValues: IReportFormModel,
  ): Promise<{
    file: IReportFileData | null;
    fileEN: IReportFileData | null;
  }> => {
    const [fileNew, fileENNew] = await Promise.all([
      uploadReportFile(reportValues?.file),
      uploadReportFile(reportValues?.file_en),
    ]);
    return {
      file: fileNew,
      fileEN: fileENNew,
    };
  };

  const uploadImageFileData = async (fileData?: IReportFileData | null): Promise<IReportFileData | null> => {
    if (!fileData) {
      return null;
    }

    if (!fileData?.key || !fileData?.file) {
      return { ...fileData, file: null };
    }

    await putObjectSG({ key: fileData.key, file: fileData.file });
    const urlMap = await getObjectUrlSG({ key: fileData.key, sign: false });
    return { ...fileData, url: urlMap?.url || '', file: null };
  };

  const uploadCoverAndCreatorAvatarByValues = async (reportValues: IReportFormModel): Promise<IReportFormModel> => {
    const [coverNew, creatorAvatarNew, collaboratorsNew] = await Promise.all([
      uploadImageFileData(reportValues?.cover),
      uploadImageFileData(reportValues?.creator_avatar),
      Promise.all(
        (reportValues?.collaborators || []).map(async (collaborator) => ({
          ...collaborator,
          creator_avatar: await uploadImageFileData(collaborator.creator_avatar),
        })),
      ),
    ]);

    return {
      ...reportValues,
      cover: coverNew,
      creator_avatar: creatorAvatarNew,
      collaborators: collaboratorsNew,
    };
  };

  const handlePublish = async () => {
    try {
      const valuesNew = await uploadCoverAndCreatorAvatarByValues(values);

      form.batch(() => {
        form.change('cover', valuesNew.cover);
        form.change('creator_avatar', valuesNew.creator_avatar);
        form.change('collaborators', valuesNew.collaborators);
      });

      const report = buildReport(valuesNew, { permissionOptions, reportHelperInfo: reportHelperInfo.current });
      onPublish?.(report);
      setSubmitted(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handlePublishAfterValidate = async () => {
    setSubmitted(true);
    const errors = validate(values);
    Object.keys(errors).forEach((fieldName) => {
      form.blur(fieldName);
    });

    if (isNotEmpty(errors)) {
      return false;
    }

    await handlePublish();
    return true;
  };

  const addCollaborator = () => {
    if (collaborators.length >= MAX_COLLABORATOR_COUNT) return;
    reportHelperInfo.current.collaborators = [
      ...reportHelperInfo.current.collaborators,
      { creator_name: '', creator_department: '' },
    ];
    form.change('collaborators', [...collaborators, createEmptyCollaborator()]);
  };

  const updateCollaborator = (index: number, patch: Partial<IReportCollaboratorFormModel>) => {
    const currentCollaborators = (form.getState().values?.collaborators || []) as IReportCollaboratorFormModel[];
    const collaboratorHelperInfo = reportHelperInfo.current.collaborators[index] || {
      creator_name: '',
      creator_department: '',
    };

    if (patch.creator_name !== undefined || patch.creator_department !== undefined) {
      reportHelperInfo.current.collaborators[index] = {
        ...collaboratorHelperInfo,
        ...(patch.creator_name !== undefined ? { creator_name: patch.creator_name } : {}),
        ...(patch.creator_department !== undefined ? { creator_department: patch.creator_department } : {}),
      };
    }

    form.change(
      'collaborators',
      currentCollaborators.map((collaborator, collaboratorIndex) =>
        collaboratorIndex === index ? { ...collaborator, ...patch } : collaborator,
      ),
    );
  };

  const updateCollaboratorInformation = async (index: number, creatorUserName: string) => {
    if (!creatorUserName) return;

    const res = await fetchUserInformation({ username: creatorUserName });
    const resData = res?.data;
    const userFullName = resData?.full_name || '';
    const userName = resData?.user_name || '';
    const departmentName = resData?.department_name || '';
    const currentCollaborators = (form.getState().values?.collaborators || []) as IReportCollaboratorFormModel[];

    if (currentCollaborators[index]?.open_id !== creatorUserName) return;

    reportHelperInfo.current.collaborators[index] = {
      creator_name: userFullName || userName || creatorUserName,
      creator_department: departmentName,
    };

    updateCollaborator(index, {
      creator_name_en: creatorUserName,
      creator_department_en: departmentName,
    });
  };

  const updateCollaboratorInformationDebounce = (index: number, creatorUserName: string) => {
    window.clearTimeout(collaboratorInformationTimers.current[index]);
    collaboratorInformationTimers.current[index] = window.setTimeout(() => {
      updateCollaboratorInformation(index, creatorUserName);
    }, 600);
  };

  const deleteCollaborator = (index: number) => {
    reportHelperInfo.current.collaborators = reportHelperInfo.current.collaborators.filter(
      (_, collaboratorIndex) => collaboratorIndex !== index,
    );
    form.change(
      'collaborators',
      collaborators.filter((_, collaboratorIndex) => collaboratorIndex !== index),
    );
  };

  const updatePrimaryAuthor = (patch: Partial<IReportCollaboratorFormModel>) => {
    if (patch.creator_name !== undefined) reportHelperInfo.current.creator_name = patch.creator_name;
    if (patch.creator_department !== undefined) reportHelperInfo.current.creator_department = patch.creator_department;
    form.batch(() => {
      if (patch.open_id !== undefined) form.change('creator_name_en', patch.open_id);
      if (patch.creator_avatar !== undefined) form.change('creator_avatar', patch.creator_avatar);
      if (patch.creator_department_en !== undefined) form.change('creator_department_en', patch.creator_department_en);
    });
  };

  const clearPrimaryAuthor = () => {
    reportHelperInfo.current.creator_name = '';
    reportHelperInfo.current.creator_department = '';
    form.batch(() => {
      form.change('creator_name_en', '');
      form.change('creator_avatar', null);
      form.change('creator_department_en', '');
    });
  };

  const reset = () => {
    reportHelperInfo.current.report_language = '';
    reportHelperInfo.current.report_language_en = '';
    reportHelperInfo.current.file_cn_language = '';
    reportHelperInfo.current.file_en_language = '';
    reportHelperInfo.current.creator_name = '';
    reportHelperInfo.current.creator_department = '';
    reportHelperInfo.current.collaborators = [];

    form.restart(DEFAULT_FORM_MODEL);

    setReportShowFileTipsMap({ showFileTips: false, showFileENTips: false });
    setSubmitSuccess(false);
    setSubmitted(false);
  };

  useImperativeHandle(ref, () => ({
    preview: (report) => {
      setPreviewVisible(true);
      setPreviewReport({
        ...report,
        ...buildReport(values, { permissionOptions, reportHelperInfo: reportHelperInfo.current }),
      });
    },
    initialize: (report) => {
      const system = report?.system || '';
      const source = report?.source || '';
      const fileName = report?.file_name || '';
      const fileENName = report?.file_name_en || '';
      const { filePathCN: originalFilePath, filePathEN: originalFileENPath } = parseReportOriginalFilePaths(
        report?.original_file_name,
      );
      const coverURL = getNullableURL(report?.cover);
      const creatorAvatarURL = getNullableURL(report?.creator_head_picture);
      const coverKey = decodeURI(coverURL?.pathname || report?.cover || '');
      const creatorAvatarKey = decodeURI(creatorAvatarURL?.pathname || report?.creator_head_picture || '');
      const collaboratorFormModels = (Array.isArray(report?.collaborators) ? report.collaborators : [])
        .slice(0, MAX_COLLABORATOR_COUNT)
        .map((collaborator) => {
          const { creator_head_picture: collaboratorAvatar, ...collaboratorInfo } = collaborator;
          const collaboratorAvatarURL = getNullableURL(collaboratorAvatar);
          const collaboratorAvatarKey = decodeURI(collaboratorAvatarURL?.pathname || collaboratorAvatar || '');
          return {
            ...collaboratorInfo,
            open_id: collaborator.open_id || '',
            creator_avatar: collaboratorAvatar
              ? {
                key: collaboratorAvatarKey,
                url: collaboratorAvatarURL?.href || '',
                file_name: getFileNameFromPath(collaboratorAvatarKey),
                file: null,
              }
              : null,
          };
        });

      const { fileCnLang, fileEnLang } = getReportLanguages(report || {});
      reportHelperInfo.current.file_cn_language = fileCnLang;
      reportHelperInfo.current.file_en_language = fileEnLang;
      reportHelperInfo.current.report_language = report?.report_language || '';
      reportHelperInfo.current.report_language_en = report?.report_language_en || '';
      reportHelperInfo.current.creator_name = report?.creator_name || '';
      reportHelperInfo.current.creator_department = report?.creator_department || '';
      reportHelperInfo.current.collaborators = collaboratorFormModels.map((collaborator) => ({
        creator_name: collaborator.creator_name || '',
        creator_department: collaborator.creator_department || '',
      }));

      const reportFormModel: IReportFormModel = {
        file: buildReportFileData(fileName, originalFilePath),
        file_en: buildReportFileData(fileENName, originalFileENPath),
        title: report?.title || '',
        title_en: report?.title_en || '',
        introduction: report?.introduction || '',
        introduction_en: report?.introduction_en || '',
        tag: report?.tag || '',
        tag_en: report?.tag_en || '',
        cover: {
          key: coverKey,
          url: coverURL?.href || '',
          file_name: getFileNameFromPath(coverKey),
          file: null,
        },
        location: !system || !source ? [] : [system, source],
        permission: formatPermissions(report?.permission, permissionOptions),
        creator_name_en: report?.creator_name_en || '',
        creator_avatar: {
          key: creatorAvatarKey,
          url: creatorAvatarURL?.href || '',
          file_name: getFileNameFromPath(creatorAvatarKey),
          file: null,
        },
        creator_department_en: report?.creator_department_en || '',
        collaborators: collaboratorFormModels,
        download_enable: report?.download_enable === 1,
        watermark_enable: report?.watermark_enable === 1,
      };

      form.initialize(reportFormModel);
      setSubmitSuccess(true);
      setLocationRestrictionEnabled(true);
    },
    // Home首页入口上传报告时，执行这个方法
    initializeWhenRecognizedReport: (file, result) => {
      const reportLang = result?.data?.report_language_en || 'Chinese';
      if (reportLang === 'Chinese') {
        handleRecognize(file, result);
      } else {
        handleRecognizeEN(file, result);
      }
    },
    updateCreatorInformation: (openId) => {
      if (!!openId) {
        form.batch(() => {
          form.change('creator_name_en', openId);
        });
        updateCreatorInformationDebounce(openId);
      }
    },
  }));

  return (
    <>
      <CardSlotV2 style={style} className={classnames('dbu-report-form', className)} iconList={iconList}>
        {(!!file?.file || !!fileEN?.file) && (
          <div className="dbu-report-form__tips">
            <img src={IMG_AI_TEXT} alt="" />
            <span>{formatMessage({ id: 'dbu_report_form_tips' })}</span>
          </div>
        )}
        <Form layout="fixed" fixedLabelWidth={150}>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_file_cn' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, fileField?.meta)}
          >
            <div className="dbu-report-form__report-file-uploader">
              {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
              <ReportUploader recognize={recognizeReportService} onRecognized={handleRecognize} />
              {!!reportShowFileTipsMap?.showFileTips && (
                <span className="dbu-report-form__report-file-uploader-tips">
                  {formatMessage({ id: 'dbu_report_upload_cn_version_report' })}
                </span>
              )}
            </div>
            {!!fileName && (
              <FileNameCard
                style={{ marginTop: 20 }}
                showDelete
                name={fileName}
                onDelete={() => {
                  Modal.confirm({
                    message: formatMessage({ id: 'dbu_common_delete_confirm_title' }),
                    description: formatMessage({ id: 'dbu_common_delete_confirm_desc' }),
                    okText: formatMessage({ id: 'dbh_confirm' }),
                    cancelText: formatMessage({ id: 'dbh_cancel' }),
                    onOk: () => {
                      if (!fileEN) {
                        reset();
                        return;
                      }

                      form.batch(() => {
                        form.change('file', null);
                        form.change('title', '');
                        form.change('introduction', '');
                        form.change('tag', '');
                        form.change('cover', null);
                      });

                      setReportShowFileTipsMap({ ...reportShowFileTipsMap, showFileTips: true });
                    },
                  });
                }}
              />
            )}
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_file_en' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, fileENField?.meta)}
          >
            <div className="dbu-report-form__report-file-uploader">
              {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
              <ReportUploader recognize={recognizeReportService} onRecognized={handleRecognizeEN} />
              {!!reportShowFileTipsMap?.showFileENTips && (
                <span className="dbu-report-form__report-file-uploader-tips">
                  {formatMessage({ id: 'dbu_report_upload_en_version_report' })}
                </span>
              )}
            </div>
            {!!fileENName && (
              <FileNameCard
                style={{ marginTop: 20 }}
                showDelete
                name={fileENName}
                onDelete={() => {
                  Modal.confirm({
                    message: formatMessage({ id: 'dbu_common_delete_confirm_title' }),
                    description: formatMessage({ id: 'dbu_common_delete_confirm_desc' }),
                    okText: formatMessage({ id: 'dbh_confirm' }),
                    onOk: () => {
                      if (!file) {
                        reset();
                        return;
                      }

                      form.batch(() => {
                        form.change('file_en', null);
                        form.change('title_en', '');
                        form.change('introduction_en', '');
                        form.change('tag_en', '');
                        form.change('cover', null);
                      });

                      setReportShowFileTipsMap({ ...reportShowFileTipsMap, showFileENTips: true });
                    },
                  });
                }}
              />
            )}
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_title_cn' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, titleField?.meta)}
          >
            <Input {...titleField.input} placeholder={formatMessage({ id: 'dbu_report_title_placeholder_cn' })} />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_title_en' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, titleENField?.meta)}
          >
            <Input {...titleENField.input} placeholder={formatMessage({ id: 'dbu_report_title_placeholder_en' })} />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_abstract_cn' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, introductionField?.meta)}
          >
            <TextArea
              {...introductionField.input}
              placeholder={formatMessage({ id: 'dbu_report_abstract_placeholder_cn' })}
            />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_abstract_en' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, introductionENField?.meta)}
          >
            <TextArea
              {...introductionENField.input}
              placeholder={formatMessage({ id: 'dbu_report_abstract_placeholder_en' })}
            />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_tag_cn' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, tagField?.meta)}
          >
            <Input {...tagField.input} placeholder={formatMessage({ id: 'dbu_report_tag_placeholder' })} />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_tag_en' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, tagENField?.meta)}
          >
            <Input {...tagENField.input} placeholder={formatMessage({ id: 'dbu_report_tag_placeholder' })} />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_cover' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, coverField?.meta)}
          >
            <ImageUploader
              className={classnames('dbu-report-form__cover-image-uploader', {
                error: submitted && !coverUrl,
              })}
              loading={coverLoading}
              url={coverUrl}
              enableDropUpload
              showPreviewUploadTrigger
              onUpload={(file, url) => {
                const fileName = file?.name || '';

                form.batch(() => {
                  form.change('cover', {
                    key: `${DBI_IMG_COS_PATH}/${Date.now()}_cover-${fileName}`,
                    url,
                    file_name: fileName,
                    file,
                  });
                });

                onUploadCover?.(file);
              }}
              onImageLoadError={() => {
                form.change('cover', null);
              }}
              onClear={() => {
                form.change('cover', null);
              }}
            />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_location' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, locationField?.meta)}
          >
            <LocationSelect
              {...locationField.input}
              options={locationOptionsWithDisabled}
              value={locationField.input.value || []}
            />
          </Form.Item>
          <Form.Item
            label={formatMessage({ id: 'dbu_report_permission' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, permissionField?.meta)}
          >
            <PermissionSelect
              options={permissionOptions}
              value={Array.isArray(permissionField.input.value) ? permissionField.input.value : []}
              onChange={(nextValue) => {
                permissionField.input.onChange(Array.isArray(nextValue) ? nextValue : []);
              }}
              onBlur={permissionField.input.onBlur}
            />
          </Form.Item>
          <Form.Item required label={formatMessage({ id: 'dbu_report_author' })}>
            <div className="dbu-report-form__author-section">
              <CollaboratorEditor
                authorNumber={1}
                value={primaryAuthor}
                submitted={submitted}
                loading={creatorAvatarLoading}
                canAdd={!collaborators.length}
                onChange={updatePrimaryAuthor}
                onOpenIdChange={(openId) => {
                  updateCreatorInformationDebounce(openId);
                }}
                onAdd={addCollaborator}
                onDelete={clearPrimaryAuthor}
                onAvatarUpload={onUploadCreatorAvatar}
              />
              {collaborators.map((collaborator, index) => (
                <CollaboratorEditor
                  key={index}
                  authorNumber={index + 2}
                  value={collaborator}
                  submitted={submitted}
                  canAdd={index === collaborators.length - 1 && collaborators.length < MAX_COLLABORATOR_COUNT}
                  isOpenIdDuplicate={
                    !!collaborator.open_id
                    && authorOpenIds.indexOf(collaborator.open_id) !== authorOpenIds.lastIndexOf(collaborator.open_id)
                    && authorOpenIds.lastIndexOf(collaborator.open_id) === index + 1
                  }
                  onChange={(patch) => updateCollaborator(index, patch)}
                  onOpenIdChange={(openId) => {
                    updateCollaboratorInformationDebounce(index, openId);
                  }}
                  onAdd={addCollaborator}
                  onDelete={() => deleteCollaborator(index)}
                />
              ))}
            </div>
          </Form.Item>
          <Form.Item label={formatMessage({ id: 'dbu_report_download_enable' })}>
            <Switch {...downloadEnableField.input} />
          </Form.Item>
          <Form.Item label={formatMessage({ id: 'dbu_report_watermark_enable' })}>
            <Switch {...watermarkEnableField.input} />
          </Form.Item>
        </Form>
        <Form.Action>
          <Button
            type="primary"
            loading={submitting}
            disabled={!formAvailable}
            onClick={(e) => {
              e?.stopPropagation();
              handleSubmitInPage();
            }}
          >
            {formatMessage({ id: 'dbu_common_save' })}
          </Button>
          <Button
            onClick={(e) => {
              e?.stopPropagation();
              onBack?.();
              history.goBack();
            }}
          >
            {formatMessage({ id: 'dbu_common_back' })}
          </Button>
          <Button
            loading={previewLoading}
            disabled={!formValid}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={async (e) => {
              e?.stopPropagation();
              try {
                setPreviewLoading(true);
                let valuesNew = { ...values };
                const uploadedReportFiles = await uploadFileAndFileEN(valuesNew);
                const fileNew = uploadedReportFiles.file;
                const fileENNew = uploadedReportFiles.fileEN;
                valuesNew = { ...valuesNew, file: fileNew, file_en: fileENNew };

                form.batch(() => {
                  form.change('file', fileNew);
                  form.change('file_en', fileENNew);
                });

                setPreviewVisible(true);
                setPreviewReport(
                  buildReport(valuesNew, { permissionOptions, reportHelperInfo: reportHelperInfo.current }),
                );
              } catch (error) {
                console.error(error);
              } finally {
                setPreviewLoading(false);
                onPreview?.();
              }
            }}
          >
            {formatMessage({ id: 'dbu_common_preview' })}
          </Button>
          <Button
            loading={reportPublishLoading}
            disabled={!submitSuccess}
            onClick={(e) => {
              e?.stopPropagation();
              void handlePublishAfterValidate();
            }}
          >
            {formatMessage({ id: 'dbu_common_publish' })}
          </Button>
        </Form.Action>
      </CardSlotV2>
      <ReportPreviewModal
        visible={previewVisible}
        disabledShare={!submitSuccess}
        report={previewReport}
        onClose={() => {
          setPreviewVisible(false);
          setPreviewReport(null);
        }}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onShare={async () => {
          const isPublished = await handlePublishAfterValidate();
          if (!isPublished) {
            return;
          }

          setPreviewVisible(false);
          setPreviewReport(null);
        }}
      />
    </>
  );
};

// @ts-ignore
export default React.forwardRef(ReportForm);
